import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { LinkItem, CampaignItem, LinkMetadata, PreviewMode } from '../types';
import { Card, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Switch } from '../components/ui/Switch';
import { SocialPreviewCard } from '../components/SocialPreviewCard';
import { LinkSuccessModal } from '../components/LinkSuccessModal';
import { QRCodeModal } from '../components/QRCodeModal';
import {
  Link as LinkIcon,
  Sparkles,
  Check,
  AlertCircle,
  ExternalLink,
  Share2,
  Calendar,
  Layers,
  ArrowLeft,
  Sliders,
  Eye,
  RefreshCw,
} from 'lucide-react';
import { validateShortcode, validateDestinationUrl, generateRandomShortcode } from '../../worker/services/shortcode-validator';
import { buildDestinationUrl } from '../lib/utils';
import { useDebounce } from '../hooks/useDebounce';
import { useToast } from '../hooks/useToast';

interface LinkEditorProps {
  linkId?: string | null;
  onNavigate: (path: string) => void;
}

export const LinkEditor: React.FC<LinkEditorProps> = ({ linkId, onNavigate }) => {
  const { showToast } = useToast();
  const isEditing = Boolean(linkId);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [campaigns, setCampaigns] = useState<CampaignItem[]>([]);

  // Form Fields
  const [destinationUrl, setDestinationUrl] = useState('');
  const [shortCode, setShortCode] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const [status, setStatus] = useState<'active' | 'disabled'>('active');
  const [expiresAt, setExpiresAt] = useState('');

  // UTM Parameters
  const [showUtmBuilder, setShowUtmBuilder] = useState(false);
  const [utmSource, setUtmSource] = useState('');
  const [utmMedium, setUtmMedium] = useState('');
  const [utmCampaign, setUtmCampaign] = useState('');
  const [utmTerm, setUtmTerm] = useState('');
  const [utmContent, setUtmContent] = useState('');

  // Social Metadata
  const [previewMode, setPreviewMode] = useState<PreviewMode>('destination');
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [ogTitle, setOgTitle] = useState('');
  const [ogDescription, setOgDescription] = useState('');
  const [ogImageUrl, setOgImageUrl] = useState('');
  const [twitterCard, setTwitterCard] = useState('summary_large_image');

  // Shortcode Live Availability Check
  const debouncedShortCode = useDebounce(shortCode, 350);
  const [isCheckingCode, setIsCheckingCode] = useState(false);
  const [codeAvailability, setCodeAvailability] = useState<{
    checked: boolean;
    available: boolean;
    reason?: string;
  }>({ checked: false, available: true });

  // Post-Creation Success Modal
  const [createdLink, setCreatedLink] = useState<LinkItem | null>(null);
  const [qrModalData, setQrModalData] = useState<{ shortCode: string; title: string } | null>(null);

  // Fetch campaigns for dropdown
  useEffect(() => {
    api.get<{ items: CampaignItem[] }>('/campaigns')
      .then((data) => setCampaigns(data.items || []))
      .catch((err) => console.error('Failed to load campaigns:', err));
  }, []);

  // Fetch link data if editing
  useEffect(() => {
    if (!linkId) return;

    setIsLoading(true);
    api.get<{ link: LinkItem }>(`/links/${linkId}`)
      .then((data) => {
        const link = data.link;
        setDestinationUrl(link.destination_url);
        setShortCode(link.short_code);
        setTitle(link.title);
        setDescription(link.description || '');
        setCampaignId(link.campaign_id || '');
        setStatus(link.status === 'active' ? 'active' : 'disabled');
        setExpiresAt(link.expires_at ? link.expires_at.slice(0, 10) : '');

        if (link.metadata) {
          setPreviewMode(link.metadata.preview_mode);
          setMetaTitle(link.metadata.meta_title || '');
          setMetaDescription(link.metadata.meta_description || '');
          setOgTitle(link.metadata.og_title || '');
          setOgDescription(link.metadata.og_description || '');
          setOgImageUrl(link.metadata.og_image_url || '');
          setTwitterCard(link.metadata.twitter_card || 'summary_large_image');
        }
      })
      .catch((err) => {
        showToast('Failed to load link', err.message, 'error');
        onNavigate('/admin/links');
      })
      .finally(() => setIsLoading(false));
  }, [linkId, showToast, onNavigate]);

  // Live debounced shortcode availability check
  useEffect(() => {
    if (isEditing) return; // Do not check existing shortcode during editing

    if (!debouncedShortCode.trim()) {
      setCodeAvailability({ checked: false, available: true });
      return;
    }

    const validation = validateShortcode(debouncedShortCode);
    if (!validation.valid) {
      setCodeAvailability({
        checked: true,
        available: false,
        reason: validation.error,
      });
      return;
    }

    setIsCheckingCode(true);
    api.get<{ available: boolean; reason?: string }>(
      `/links/check-shortcode?code=${encodeURIComponent(validation.normalizedCode)}`
    )
      .then((res) => {
        setCodeAvailability({
          checked: true,
          available: res.available,
          reason: res.reason,
        });
      })
      .catch(() => {
        setCodeAvailability({ checked: false, available: true });
      })
      .finally(() => setIsCheckingCode(false));
  }, [debouncedShortCode, isEditing]);

  // Handle Campaign Selection Auto-Fill UTMs
  const handleCampaignChange = (cId: string) => {
    setCampaignId(cId);
    if (cId) {
      const selected = campaigns.find((c) => c.id === cId);
      if (selected) {
        if (selected.utm_source) setUtmSource(selected.utm_source);
        if (selected.utm_medium) setUtmMedium(selected.utm_medium);
        if (selected.utm_campaign) setUtmCampaign(selected.utm_campaign);
      }
    }
  };

  const handleGenerateRandomCode = () => {
    const code = generateRandomShortcode(6);
    setShortCode(code);
  };

  const computedFinalDestination = buildDestinationUrl({
    baseUrl: destinationUrl,
    source: utmSource,
    medium: utmMedium,
    campaign: utmCampaign,
    term: utmTerm,
    content: utmContent,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 1. Validate destination URL
    const destValidation = validateDestinationUrl(computedFinalDestination);
    if (!destValidation.valid) {
      showToast('Invalid Destination URL', destValidation.error, 'error');
      return;
    }

    // 2. Validate shortcode
    const codeValidation = validateShortcode(shortCode);
    if (!codeValidation.valid) {
      showToast('Invalid Shortcode', codeValidation.error, 'error');
      return;
    }

    if (!title.trim()) {
      showToast('Title Required', 'Please enter a descriptive internal title', 'error');
      return;
    }

    try {
      setIsSaving(true);

      const payload = {
        short_code: codeValidation.normalizedCode,
        destination_url: destValidation.normalizedUrl,
        title: title.trim(),
        description: description.trim() || null,
        campaign_id: campaignId || null,
        status: status,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
        metadata: {
          preview_mode: previewMode,
          meta_title: metaTitle.trim() || title.trim(),
          meta_description: metaDescription.trim() || description.trim() || null,
          og_title: ogTitle.trim() || metaTitle.trim() || title.trim(),
          og_description: ogDescription.trim() || metaDescription.trim() || description.trim() || null,
          og_image_url: ogImageUrl.trim() || null,
          og_type: 'website',
          twitter_title: ogTitle.trim() || title.trim(),
          twitter_description: ogDescription.trim() || null,
          twitter_image_url: ogImageUrl.trim() || null,
          twitter_card: twitterCard,
        },
      };

      if (isEditing && linkId) {
        const res = await api.put<{ link: LinkItem }>(`/links/${linkId}`, payload);
        showToast('Link Updated', `Changes saved for /${res.link.short_code}`, 'success');
        onNavigate('/admin/links');
      } else {
        const res = await api.post<{ link: LinkItem }>('/links', payload);
        setCreatedLink(res.link);
      }
    } catch (err: any) {
      showToast('Save Failed', err.message, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6 animate-in fade-in duration-300 pb-12">
      {/* Top Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => onNavigate('/admin/links')}
          className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-white transition-colors cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Links</span>
        </button>

        <span className="text-xs px-2.5 py-1 rounded-lg bg-slate-900 border border-slate-800 text-slate-400">
          Domain: <strong className="text-orange-400 font-mono">lnk.brototype.com</strong>
        </span>
      </div>

      <div>
        <h1 className="text-2xl font-extrabold text-white tracking-tight">
          {isEditing ? 'Edit Short Link' : 'Create Custom Short Link'}
        </h1>
        <p className="text-xs text-slate-400 mt-1">
          {isEditing
            ? 'Updating destination will immediately redirect new visitors without changing the public URL.'
            : 'Configure your branded marketing URL, campaign associations, and social media preview cards.'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Core Link Details Card */}
        <Card>
          <CardHeader>
            <div>
              <CardTitle>Destination & Shortcode</CardTitle>
              <CardDescription>Primary redirection mapping</CardDescription>
            </div>
          </CardHeader>

          <div className="space-y-5">
            {/* Destination URL */}
            <div>
              <Input
                label="Destination Target URL"
                required
                placeholder="https://www.brototype.com/ai-builder-challenge/"
                value={destinationUrl}
                onChange={(e) => setDestinationUrl(e.target.value)}
                hint="Full HTTPS or HTTP URL where visitors will be redirected (302 Found)."
              />
            </div>

            {/* Custom Shortcode with live availability check */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider">
                Custom Shortcode <span className="text-orange-400">*</span>
              </label>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Input
                    prefixText="lnk.brototype.com/"
                    placeholder="ai-challenge"
                    required
                    disabled={isEditing}
                    value={shortCode}
                    onChange={(e) => setShortCode(e.target.value.toLowerCase().trim())}
                  />
                  {/* Availability feedback indicator */}
                  {!isEditing && shortCode.trim().length >= 2 && (
                    <div className="absolute right-3 top-2.5 flex items-center gap-1 text-xs">
                      {isCheckingCode ? (
                        <RefreshCw className="w-4 h-4 animate-spin text-slate-400" />
                      ) : codeAvailability.checked && codeAvailability.available ? (
                        <span className="flex items-center gap-1 text-emerald-400 font-medium">
                          <Check className="w-4 h-4" /> Available
                        </span>
                      ) : codeAvailability.checked && !codeAvailability.available ? (
                        <span className="flex items-center gap-1 text-rose-400 font-medium">
                          <AlertCircle className="w-4 h-4" /> {codeAvailability.reason || 'Unavailable'}
                        </span>
                      ) : null}
                    </div>
                  )}
                </div>

                {!isEditing && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="md"
                    onClick={handleGenerateRandomCode}
                    leftIcon={<Sparkles className="w-4 h-4 text-amber-400" />}
                    title="Generate cryptographically secure random shortcode"
                  >
                    Random
                  </Button>
                )}
              </div>
              <p className="text-[11px] text-slate-500">
                Letters, numbers, and hyphens (2-50 characters). Automatically lowercased.
              </p>
            </div>

            {/* Title & Campaign Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Input
                  label="Link Title"
                  required
                  placeholder="e.g. AI Builder Challenge — Instagram Bio"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  hint="Internal descriptive name for marketing reports."
                />
              </div>

              <div className="space-y-1.5">
                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider">
                  Marketing Campaign
                </label>
                <select
                  value={campaignId}
                  onChange={(e) => handleCampaignChange(e.target.value)}
                  className="w-full px-3.5 py-2 text-sm bg-slate-900 text-slate-200 border border-slate-700 rounded-xl focus:outline-none focus:border-orange-500/80 cursor-pointer"
                >
                  <option value="">No Campaign (Independent Link)</option>
                  {campaigns.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-slate-500">Groups clicks under campaign analytics.</p>
              </div>
            </div>

            {/* Description & Expiration */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Input
                  label="Internal Description (Optional)"
                  placeholder="Target audience, influencer name, or specific promo details"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              <div>
                <Input
                  type="date"
                  label="Link Expiration Date (Optional)"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  hint="After this date, visitors will receive HTTP 410 Gone."
                />
              </div>
            </div>

            {/* Link Status */}
            <div className="pt-2 border-t border-slate-800 flex items-center justify-between">
              <Switch
                checked={status === 'active'}
                onChange={(active) => setStatus(active ? 'active' : 'disabled')}
                label="Active Status"
                description={
                  status === 'active'
                    ? 'Link is active and redirecting incoming traffic.'
                    : 'Link is disabled and will return HTTP 410 Gone.'
                }
              />
            </div>
          </div>
        </Card>

        {/* UTM Parameter Builder Accordion */}
        <Card>
          <div
            onClick={() => setShowUtmBuilder(!showUtmBuilder)}
            className="flex items-center justify-between cursor-pointer select-none"
          >
            <div>
              <CardTitle className="flex items-center gap-2">
                <Sliders className="w-4 h-4 text-orange-400" />
                <span>Campaign UTM Parameter Builder</span>
              </CardTitle>
              <CardDescription>
                Tag your links with Google Analytics and Meta Ads tracking tags
              </CardDescription>
            </div>
            <span className="text-xs font-semibold text-orange-400 hover:underline">
              {showUtmBuilder ? 'Hide Parameters' : 'Configure UTMs'}
            </span>
          </div>

          {showUtmBuilder && (
            <div className="mt-5 pt-4 border-t border-slate-800 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Input
                  label="utm_source"
                  placeholder="e.g. instagram, meta_ads, youtube"
                  value={utmSource}
                  onChange={(e) => setUtmSource(e.target.value)}
                />
                <Input
                  label="utm_medium"
                  placeholder="e.g. social, bio, story, cpc"
                  value={utmMedium}
                  onChange={(e) => setUtmMedium(e.target.value)}
                />
                <Input
                  label="utm_campaign"
                  placeholder="e.g. ai-builder-2026"
                  value={utmCampaign}
                  onChange={(e) => setUtmCampaign(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  label="utm_term (Optional)"
                  placeholder="e.g. python-ai-course"
                  value={utmTerm}
                  onChange={(e) => setUtmTerm(e.target.value)}
                />
                <Input
                  label="utm_content (Optional)"
                  placeholder="e.g. banner-v1, video-cta"
                  value={utmContent}
                  onChange={(e) => setUtmContent(e.target.value)}
                />
              </div>

              {/* Resolved Destination Preview */}
              <div className="p-3 rounded-xl bg-slate-950 border border-slate-800 text-xs">
                <span className="text-slate-400 font-semibold block mb-1">Final Resolved URL Preview:</span>
                <span className="font-mono text-orange-300 break-all select-all">
                  {computedFinalDestination || 'Enter destination URL above to preview'}
                </span>
              </div>
            </div>
          )}
        </Card>

        {/* Social Sharing Metadata & Card Preview */}
        <Card>
          <CardHeader>
            <div>
              <CardTitle className="flex items-center gap-2">
                <Share2 className="w-4 h-4 text-orange-400" />
                <span>Custom Social Sharing Metadata (Open Graph & Twitter)</span>
              </CardTitle>
              <CardDescription>
                Configure how this link appears when shared on WhatsApp, Facebook, LinkedIn, or Twitter/X
              </CardDescription>
            </div>
          </CardHeader>

          <div className="space-y-6">
            {/* Preview Mode Selector */}
            <div className="flex items-center gap-4 p-3 rounded-xl bg-slate-950 border border-slate-800">
              <label className="flex items-center gap-2 text-xs font-medium cursor-pointer">
                <input
                  type="radio"
                  name="preview_mode"
                  checked={previewMode === 'destination'}
                  onChange={() => setPreviewMode('destination')}
                  className="accent-orange-500"
                />
                <span className="text-slate-200">Destination Preview (Standard redirect)</span>
              </label>

              <label className="flex items-center gap-2 text-xs font-medium cursor-pointer">
                <input
                  type="radio"
                  name="preview_mode"
                  checked={previewMode === 'custom'}
                  onChange={() => setPreviewMode('custom')}
                  className="accent-orange-500"
                />
                <span className="text-orange-400 font-semibold">Custom Brototype Preview (Serve custom Open Graph tags)</span>
              </label>
            </div>

            {previewMode === 'custom' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pt-2">
                {/* Form fields on the left */}
                <div className="space-y-4">
                  <Input
                    label="Open Graph / Social Title"
                    placeholder="e.g. AI Builder Challenge — Brototype"
                    value={ogTitle}
                    onChange={(e) => setOgTitle(e.target.value)}
                    hint="Headline shown in bold on social cards."
                  />

                  <div className="space-y-1.5">
                    <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider">
                      Social Card Description
                    </label>
                    <textarea
                      rows={3}
                      value={ogDescription}
                      onChange={(e) => setOgDescription(e.target.value)}
                      placeholder="e.g. Build real software using AI with free technical support from Brototype mentors."
                      className="w-full px-3.5 py-2 text-sm bg-slate-900 text-slate-100 placeholder:text-slate-500 border border-slate-700 rounded-xl focus:outline-none focus:border-orange-500/80"
                    />
                  </div>

                  <Input
                    label="Preview Banner Image URL (1200 x 630 px)"
                    placeholder="https://.../banner.jpg"
                    value={ogImageUrl}
                    onChange={(e) => setOgImageUrl(e.target.value)}
                    hint="Publicly accessible HTTPS image URL (1.91:1 ratio recommended)."
                  />

                  <div className="space-y-1.5">
                    <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider">
                      Twitter Card Format
                    </label>
                    <select
                      value={twitterCard}
                      onChange={(e) => setTwitterCard(e.target.value)}
                      className="w-full px-3.5 py-2 text-sm bg-slate-900 text-slate-200 border border-slate-700 rounded-xl focus:outline-none focus:border-orange-500/80 cursor-pointer"
                    >
                      <option value="summary_large_image">summary_large_image (Large banner card)</option>
                      <option value="summary">summary (Compact thumbnail)</option>
                    </select>
                  </div>
                </div>

                {/* Live Interactive Social Card Preview on the right */}
                <div>
                  <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">
                    Live Preview Simulation
                  </label>
                  <SocialPreviewCard
                    title={title}
                    destinationUrl={destinationUrl}
                    metadata={{
                      og_title: ogTitle,
                      og_description: ogDescription,
                      og_image_url: ogImageUrl,
                      twitter_card: twitterCard,
                      preview_mode: previewMode,
                    }}
                    shortCode={shortCode || 'code'}
                  />
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Action Button Row */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-800">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onNavigate('/admin/links')}
          >
            Cancel
          </Button>

          <Button
            type="submit"
            variant="primary"
            isLoading={isSaving}
          >
            {isEditing ? 'Save Link Changes' : 'Create Custom Short Link'}
          </Button>
        </div>
      </form>

      {/* Success Modal */}
      {createdLink && (
        <LinkSuccessModal
          isOpen={!!createdLink}
          onClose={() => {
            setCreatedLink(null);
            onNavigate('/admin/links');
          }}
          link={createdLink}
          onOpenQR={(code, title) => setQrModalData({ shortCode: code, title })}
          onEdit={(id) => {
            setCreatedLink(null);
            onNavigate(`/admin/links/${id}`);
          }}
          onCreateAnother={() => {
            setCreatedLink(null);
            setDestinationUrl('');
            setShortCode('');
            setTitle('');
            setDescription('');
            setOgTitle('');
            setOgDescription('');
            setOgImageUrl('');
          }}
        />
      )}

      {/* QR Modal */}
      {qrModalData && (
        <QRCodeModal
          isOpen={!!qrModalData}
          onClose={() => setQrModalData(null)}
          shortCode={qrModalData.shortCode}
          linkTitle={qrModalData.title}
        />
      )}
    </div>
  );
};
